import { faBirthdayCake, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { lighten } from "@mui/material/styles";
import { GridColDef } from "@mui/x-data-grid";
import { differenceInCalendarDays, getDaysInMonth } from "date-fns";
import React, { useContext, useCallback, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ClockIcon } from "src/assets/icons";
import EmailInfoPopover from "src/containers/EmailInfoPopover/EmailInfoPopover";
import SendMassNotificationsWrapper from "src/containers/MyTeamPresenceAndLeaves/TnAMassNotifications/SendMassNotificationsWrapper";
import { DIGEST_NOTIFICATION_TYPES } from "src/utils/constants/constants";
import { v4 as uuidv4 } from "uuid";

import {
  Container,
  EmployeeNameContainer,
  LastTimesheetUsageInfoContainer,
  LastUsageAndConfirmation,
  PendingApprovalsContainer,
} from "./styles";
import { useAppSelector } from "../../../../custom-hooks/redux/redux";
import useAuthorization, { isAuthorizedEeGroup } from "../../../../custom-hooks/useAuthorization/useAuthorization";
import useForceUpdate from "../../../../custom-hooks/useForceUpdate/useForceUpdate";
import { RefModel } from "../../../../models/base/ref-model";
import { EmployeeGroupRef } from "../../../../models/employee-groups/employee-group-ref";
import { EmployeeRef } from "../../../../models/employees/employee-ref";
import { WithEmployee } from "../../../../models/employees/state-employee";
import { TnAOverviewDTO } from "../../../../models/tna-overviews/tna-overview-dto";
import { TnAOverviewsFilter } from "../../../../models/tna-overviews/tna-overview.filter";
import {
  TnATimesheetConfirmationSTO,
  TnATimesheetConfirmationRef,
  TnATimesheetConfirmationsDTO,
} from "../../../../models/tna-timesheet-confirmation";
import ConcatenatedCell from "../../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import { ColumnsHeaderTitle } from "../../../../reusable-components/Table/styles";
import Table from "../../../../reusable-components/Table/Table";
import { EmployeeGroupsApiService } from "../../../../services/employee-groups/employee-groups-api.service";
import { TnAOverviewsApiService } from "../../../../services/tna-overviews/tna-overviews-api.service";
import { TnATimesheetConfirmationsApiService } from "../../../../services/tna-timesheet-confirmations/tna-timesheet-confirmations-api.service";
import { AUTHORIZATIONS } from "../../../../utils/constants/authorization-constants";
import { FT_MTPAL } from "../../../../utils/constants/filter-tables-constants";
import { intlFormatDateShort } from "../../../../utils/time-utils";
import { openTimesheetViewMode } from "../../../../views/my-t-and-a/my-ee-tna-utils";
import LoadingOverlay from "../../../LoadingOverlay/LoadingOverlay";
import { fromEmailParamsName } from "../../../NavigateToLoginPage/NavigateToLoginPage";
import { MyTnAContext } from "../../MyTeamPresenceAndLeaves";
import EmployeeNotificationsAndActivity from "../EmployeeNotificationsAndActivity/EmployeeNotificationsAndActivity";
import ExportTnAOverviewToExcel from "../ExportTnAOverviewToExcel/ExportTnAOverviewToExcel";
import PendingApprovalModal from "../PendingApprovalModal/PendingApprovalModal";
import PendingEHApprovalModal from "../PendingEHApprovalModal/PendingEHApprovalModal";
import TimesheetAnomaliesActions from "../TimesheetAnomaliesActions/TimesheetAnomaliesActions";
import TnAOverviewActions from "../TnAOverviewActions/TnAOverviewActions";
import UpdatingEmployee from "../UpdatingEmployee/UpdatingEmployee";

interface State {
  loading: boolean;
  error: string;
}

interface Props {
  getItems: (items: TnAOverviewDTO[]) => void;
}

const TnAOverviews = ({ getItems }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_dashboard";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";
  const [searchParams] = useSearchParams();
  const fromEmail = searchParams.get(fromEmailParamsName);
  const highlightedValue = searchParams.get("hl");
  const theme = useTheme();
  const allItems = useRef<TnAOverviewDTO[]>([]);

  const [reload, setReload] = useState<boolean>(false);
  const [state, setState] = useState<State>({
    loading: false,
    error: "",
  });
  const {
    legalEntity: { legalEntityOption, legalEntityQuickFilter },
    employeeGroup: { employeeGroupOption, employeeGroupQuickFilter },
    currentEmployees: { onlyCurrentEmployeesOptionValue, onlyCurrentEmployeesQuickFilter },
  } = useContext(MyTnAContext);

  const navigate = useNavigate();
  const { forceUpdate } = useForceUpdate();

  const hasAccess = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  const clickNavigate = (employee: EmployeeRef | null) => {
    const canApproveReject = allowedToApproveReject(
      employee?.groups?.map((group: EmployeeGroupRef) => group?.id) ?? []
    );

    const canViewOnly = hasAccessToViewOnly(employee?.groups?.map((group: EmployeeGroupRef) => group?.id) ?? []);

    if (!canApproveReject && canViewOnly && employee?.id) {
      openTimesheetViewMode(employee.id);
    }

    if (canApproveReject) {
      navigate("/my-t-and-a", {
        state: {
          employee,
          forManager: true,
        } as WithEmployee,
      });
    }
  };

  const { currentUserAuthorizations, currentUserNotificationPreferences } = useAppSelector(
    (state) => state.currentUserReducer
  );

  const allowedToApproveReject = useCallback(
    (groupIds: string[]) => {
      return isAuthorizedEeGroup(
        [AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves],
        currentUserAuthorizations,
        groupIds
      );
    },
    [currentUserAuthorizations]
  );

  const hasAccessToViewOnly = useCallback(
    (groupIds: string[]) => {
      return isAuthorizedEeGroup(
        [AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData],
        currentUserAuthorizations,
        groupIds
      );
    },
    [currentUserAuthorizations]
  );

  const remindRequest = useCallback(async (item: TnATimesheetConfirmationRef) => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const apiService = new TnATimesheetConfirmationsApiService();
      apiService.useSaveItemsPath("/SendSignOffReminderNotification");
      const response = await apiService.saveItems([
        {
          id: uuidv4(),
          isDeleted: false,
          employee: item.employee,
        } as TnATimesheetConfirmationSTO,
      ]);

      if (!response.error) {
        setReload(true);
      } else {
        console.log("error: ", response.error);
        setState((state) => ({ ...state, error: response.error }));
      }
    } catch (error) {
      alert(error as string);
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, []);

  const cancelRequest = useCallback(async (item: TnATimesheetConfirmationSTO) => {
    setState((state) => ({ ...state, loading: true }));

    try {
      const service = new TnATimesheetConfirmationsApiService();
      service.useSaveItemsPath("/SaveCancellationTnATimesheetConfirmations");

      const response = await service.saveItems([
        {
          ...item,
          isCanceled: true,
        } as TnATimesheetConfirmationSTO,
      ]);

      if (!response.error) {
        setReload(true);
      } else {
        console.log("error: ", response.error);
        setState((state) => ({ ...state, error: response.error }));
      }
    } catch (error) {
      alert(error as string);
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, []);

  const highlightCell = (row: TnAOverviewDTO) => {
    const tnaGroupId = row.employee?.tnAGroup?.id;
    const isApproverForThisEe =
      !!tnaGroupId &&
      isAuthorizedEeGroup(
        [AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves],
        currentUserAuthorizations,
        [tnaGroupId],
        true
      );

    if (highlightedValue === "missing-clocking" && isApproverForThisEe) {
      return row.timesheetAnomalies > 0;
    } else if (highlightedValue === "pending-sign-off" && isApproverForThisEe) {
      const pendingSince = row.lastTimesheetConfirmationRequest
        ? differenceInCalendarDays(new Date(), new Date(row.lastTimesheetConfirmationRequest))
        : null;
      const notification = currentUserNotificationPreferences.find(
        (notif) => notif.notificationType?.id === DIGEST_NOTIFICATION_TYPES.my_team_timesheet.id
      );
      const notificationSection = notification?.digestSectionsParams.find(
        (section) =>
          section.digestSection?.id ===
          DIGEST_NOTIFICATION_TYPES.my_team_timesheet.sections.PendingTimesheetSignoffForMoreThanId
      );
      const delayPreference = notificationSection?.value;
      if (pendingSince && delayPreference) {
        return pendingSince > delayPreference;
      }

      return false;
    }

    return false;
  };

  const columns: GridColDef<TnAOverviewDTO>[] = [
    {
      field: "employeeCode, internalId",
      renderHeader: () => (
        <Typography>{`${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`}</Typography>
      ),
      headerAlign: "center",
      width: 120,
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"center"}
          FirstElement={
            <Tooltip title={`${t(`${tRPCommon}.employee_code`)}`}>
              <Typography>{row.employee?.employeeCode ?? ""}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Tooltip title={`${t(`${tRPCommon}.internal_id`)}`}>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
              >
                {row.employee?.internalId ?? ""}
              </Typography>
            </Tooltip>
          }
        />
      ),
    },
    {
      field: "name",
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"left"}
          FirstElement={
            <EmployeeNameContainer>
              <Grid sx={{ display: "flex", flexDirection: "column", color: row.tnAOk ? "" : "darkgray" }}>
                <span
                  title={row.tnAOk ? row?.employee?.name : "Missing employee contractual timesheet tracking start date"}
                  onClick={() => {
                    clickNavigate(row.employee);
                  }}
                  style={{ color: row.tnAOk ? "" : "darkgray" }}
                >
                  {row?.employee?.name}
                </span>
                {row?.employee?.birthDate && (
                  <Typography variant={"body2"}>
                    <FontAwesomeIcon
                      icon={faBirthdayCake}
                      size="xs"
                    />{" "}
                    &nbsp;
                    {intlFormatDateShort(row.employee.birthDate)}
                  </Typography>
                )}
              </Grid>
            </EmployeeNameContainer>
          }
          SecondElement={
            <Grid>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
                title={row?.legalEntity?.name}
              >
                {row?.legalEntity?.code ?? row?.legalEntity?.name}
              </Typography>
            </Grid>
          }
        />
      ),
      renderHeader: () => (
        <Tooltip title={`${t("common_words.employee")} | ${t("common_words.legal_entity")}`}>
          <ColumnsHeaderTitle>{`${t("common_words.employee")} | ${t("common_words.legal_entity")}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      // headerName: `${t(`common_words.employee`)} / ${t(
      //   `common_words.legal_entity`
      // )}`,
      headerAlign: "center",
      flex: 1,
      minWidth: 50,
    },
    {
      field: "employee.tnAGroup",
      renderHeader: () => <Typography>{t("common_words.tna_employee_group")}</Typography>,
      headerAlign: "center",
      flex: 1,
      minWidth: 140,
      // sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.employee?.tnAGroup?.name}>
          <Typography
            key={row.employee?.tnAGroup?.id}
            sx={{ margin: "0 5px", color: row.tnAOk ? "" : "darkgray" }}
          >
            {row.employee?.tnAGroup?.code}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "pendingApprovals",
      renderCell: ({ row }) => {
        const eeGroupsIds = row?.employee?.groups?.map((group: EmployeeGroupRef) => group?.id) ?? [];

        return (
          <>
            {allowedToApproveReject(eeGroupsIds) || hasAccessToViewOnly(eeGroupsIds) ? (
              <PendingApprovalsContainer>
                {!!row?.pendingApprovals?.length && (
                  <PendingApprovalModal
                    employee={row.employee}
                    notifyParent={forceUpdate}
                  >
                    <Typography title={"Absences"}>{row.pendingApprovals.length} abs</Typography>
                  </PendingApprovalModal>
                )}
                {!!row?.pendingEHApprovals?.length && row.employee && (
                  <PendingEHApprovalModal
                    employee={row.employee}
                    notifyParent={forceUpdate}
                  >
                    <Typography title={"Extra hours"}>{row.pendingEHApprovals.length} eh</Typography>
                  </PendingEHApprovalModal>
                )}
              </PendingApprovalsContainer>
            ) : (
              <Grid style={{ display: "flex", flexDirection: "column" }}>
                <Typography title={"Absences"}>{row.pendingApprovals.length} abs</Typography>

                <Typography title={"Extra hours"}>{row.pendingEHApprovals.length} eh</Typography>
              </Grid>
            )}
          </>
        );
      },
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.pendingApprovals`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.pendingApprovals`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 50,
    },
    {
      field: "workedTimeBalance",
      renderCell: ({ row }) => {
        const workedTimeMoreThan: number | null =
          row?.tnAConfig?.workedTimeMoreThan === 0
            ? 0
            : row?.tnAConfig?.workedTimeMoreThan
            ? row.tnAConfig.workedTimeMoreThan
            : null;

        const workedTimeLessThan: number | null = workedTimeMoreThan ? -workedTimeMoreThan : workedTimeMoreThan;

        const highlightInRed =
          (workedTimeMoreThan !== null && row?.workedTimeBalance > workedTimeMoreThan) ||
          (workedTimeLessThan !== null && row?.workedTimeBalance < workedTimeLessThan)
            ? theme.palette.error.main
            : "inherit";

        return (
          <>
            {row.tnAOk && row?.employee?.id && (
              <>
                <UpdatingEmployee id={row.employee.id} />
                <Typography
                  sx={{
                    color: highlightInRed,
                  }}
                  title={row?.workedTimeBalance?.toFixed(2)}
                >
                  {row?.workedTimeBalance?.toFixed(2)}
                </Typography>
              </>
            )}
          </>
        );
      },
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.workedTimeBalance`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.workedTimeBalance`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 50,
    },
    {
      field: "vacationBalance",
      renderCell: ({ row }) => {
        const currDay = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());

        const day: number | null = row?.tnAConfig?.vacationBalanceAfterDay
          ? row.tnAConfig.vacationBalanceAfterDay
          : null;

        const month: number | null =
          row?.tnAConfig?.vacationBalanceAfterMonth === 0
            ? 0
            : row?.tnAConfig?.vacationBalanceAfterMonth
            ? row.tnAConfig.vacationBalanceAfterMonth
            : null;

        const deadline = day === null || month === null ? null : new Date(new Date().getFullYear(), month, day);

        const afterDeadLine: boolean | null = deadline ? currDay.getTime() - deadline.getTime() >= 0 : null;

        const highlightInRed =
          !!afterDeadLine &&
          row?.tnAConfig?.vacationBalanceMoreThan &&
          row?.vacationBalance > row.tnAConfig.vacationBalanceMoreThan
            ? theme.palette.error.main
            : "inherit";

        return (
          <>
            {row.tnAOk && row?.employee?.id && (
              <>
                <UpdatingEmployee id={row.employee.id} />
                <Typography
                  sx={{
                    color: highlightInRed,
                  }}
                  title={row?.vacationBalance?.toFixed(2)}
                >
                  {row?.vacationBalance?.toFixed(2)}
                </Typography>
              </>
            )}
          </>
        );
      },
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.vacationBalance`)} ${new Date().getFullYear()}`}>
          <ColumnsHeaderTitle>{`${t(
            `${translationFields}.vacationBalance`
          )} ${new Date().getFullYear()}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 50,
    },
    {
      field: "timesheetAnomalies",
      renderCell: ({ row }) =>
        row.tnAOk &&
        row?.employee?.id && (
          <>
            <UpdatingEmployee id={row.employee.id} />
            {row?.timesheetAnomalies > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <div
                  title={`${t(`${translationFields}.anomalies`)}`}
                  style={{
                    color: row?.timesheetAnomalies > 0 ? theme.palette.error.light : "inherit",
                  }}
                >
                  {row?.timesheetAnomalies}
                </div>
                {(!row.tnAOk ||
                  !row.employee?.id ||
                  !row.timesheetAnomalies ||
                  row.timesheetAnomalies > 0 ||
                  row.anomalyTrackings.length > 0) && (
                  <TimesheetAnomaliesActions
                    item={row}
                    handleNotifyParent={forceUpdate}
                  />
                )}
              </Box>
            )}
          </>
        ),
      renderHeader: () => (
        <>
          <Tooltip title={`${t(`${translationFields}.timesheetAnomalies`)}`}>
            <ColumnsHeaderTitle>{`${t(`${translationFields}.timesheetAnomalies`)}`}</ColumnsHeaderTitle>
          </Tooltip>
          {fromEmail && highlightedValue === "missing-clocking" && (
            <EmailInfoPopover
              autoOpen={!!fromEmail}
              message={"Only values of employees for whom you are an approver are highlighted"}
            />
          )}
        </>
      ),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 50,
      cellClassName: (params) =>
        fromEmail && highlightedValue === "missing-clocking" && highlightCell(params.row) ? "highlighted " : "",
    },
    {
      field: "abnormalTime",
      renderCell: ({ row }) =>
        row.tnAOk &&
        row?.employee?.id && (
          <>
            <UpdatingEmployee id={row.employee.id} />
            {row?.abnormalTime > 0 && (
              <div
                title={`${t(`${translationFields}.abnormalDays`)}`}
                style={{
                  color: row?.abnormalTime > 0 ? theme.palette.warning.dark : "inherit",
                }}
              >
                {row?.abnormalTime}
              </div>
            )}
          </>
        ),
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.abnormalTime`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.abnormalTime`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 50,
    },
    {
      field: "lastTimesheetConfirmation",
      renderHeader: () => (
        <>
          <Tooltip title={`${t(`${translationFields}.lastSignOff`)}`}>
            <ColumnsHeaderTitle>{`${t(`${translationFields}.lastSignOff`)}`}</ColumnsHeaderTitle>
          </Tooltip>
          {fromEmail && highlightedValue === "pending-sign-off" && (
            <EmailInfoPopover
              autoOpen={!!fromEmail}
              message={`${t(`${tRP}.highlight_approvers`)}`}
            />
          )}
        </>
      ),
      headerAlign: "center",
      flex: 1.2,
      // minWidth: 120,
      // maxWidth: 360,
      renderCell: ({ row }) => {
        const pendingSince: number | null = row.lastTimesheetConfirmationRequest
          ? differenceInCalendarDays(new Date(), new Date(row.lastTimesheetConfirmationRequest))
          : null;

        const didnotSignOffSince: number | null = row.lastTimesheetConfirmation
          ? new Date(row.lastTimesheetConfirmation).getMonth() < new Date().getMonth()
            ? differenceInCalendarDays(new Date(), new Date(row.lastTimesheetConfirmation)) -
              getDaysInMonth(new Date(row.lastTimesheetConfirmation))
            : null
          : null;

        const delayInSignOffMoreThan =
          row?.tnAConfig?.delayInSignOffMoreThan === 0
            ? 0
            : row?.tnAConfig?.delayInSignOffMoreThan
            ? row.tnAConfig.delayInSignOffMoreThan
            : null;

        const delayInSendingSignOffMoreThan =
          row?.tnAConfig?.delayInSendingSignOffMoreThan === 0
            ? 0
            : row?.tnAConfig?.delayInSendingSignOffMoreThan
            ? row.tnAConfig.delayInSendingSignOffMoreThan
            : null;

        return (
          <LastTimesheetUsageInfoContainer style={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
            {!row.tnAOk ? (
              <LastUsageAndConfirmation style={{ color: lighten(theme.palette.text.disabled, 0.2) }}>
                {`${t(`${tRP}.tool_not_activated`)}`}
              </LastUsageAndConfirmation>
            ) : !row.lastTimesheetConfirmation ? (
              <LastUsageAndConfirmation style={{ color: lighten(theme.palette.text.disabled, 0.2) }}>
                {`${t(`${tRP}.never`)}`}
              </LastUsageAndConfirmation>
            ) : (
              <LastUsageAndConfirmation>
                {`${intlFormatDateShort(new Date(row.lastTimesheetConfirmation))}`}
              </LastUsageAndConfirmation>
            )}
            <Grid style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              {row.lastTimesheetConfirmationRequest ? (
                <ClockIcon
                  color={
                    delayInSignOffMoreThan !== null && pendingSince && pendingSince > delayInSignOffMoreThan
                      ? lighten(theme.palette.error.main, 0.5)
                      : lighten(theme.palette.primary.main, 0.5)
                  }
                  title={`${t(`${tRP}.pending_timesheet_signoff`)} ${intlFormatDateShort(
                    new Date(row.lastTimesheetConfirmationRequest)
                  )}`}
                />
              ) : (
                delayInSendingSignOffMoreThan !== null &&
                !!didnotSignOffSince &&
                didnotSignOffSince > delayInSendingSignOffMoreThan && (
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    color={lighten(theme.palette.error.main, 0.5)}
                    title={`${t(`${tRP}.month_passed`)} `}
                  />
                )
              )}

              {row.tnAOk ? (
                row.lastTimesheetConfirmationRequest ? (
                  <TnAOverviewActions
                    item={row}
                    showSignoffRequest={false}
                    showReminderRequest={true}
                    showCancellRequest
                    handleNotifyParent={() => setReload(true)}
                    {...(!!row.tnATimesheetConfirmations && {
                      handleRemindRequest: () =>
                        remindRequest(row.tnATimesheetConfirmations as TnATimesheetConfirmationsDTO),
                      handleCancelRequest: () =>
                        cancelRequest(row.tnATimesheetConfirmations as TnATimesheetConfirmationsDTO),
                    })}
                  />
                ) : (
                  <TnAOverviewActions
                    item={row}
                    showSignoffRequest={true}
                    showReminderRequest={false}
                    showCancellRequest={false}
                    handleNotifyParent={() => setReload(true)}
                  />
                )
              ) : (
                <></>
              )}
            </Grid>
          </LastTimesheetUsageInfoContainer>
        );
      },
      cellClassName: (params) =>
        fromEmail && highlightedValue === "pending-sign-off" && highlightCell(params.row) ? "highlighted " : "",
    },
    {
      field: "lastUsage",
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.timesheet_usage_information`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.timesheet_usage_information`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      // flex: 3,
      // minWidth: 120,
      // maxWidth: 360,
      flex: 1.5,
      align: "center",
      renderCell: ({ row }) => {
        const diffInCalendarDays = row.lastUsage ? differenceInCalendarDays(new Date(), new Date(row.lastUsage)) : 0;
        return (
          <LastTimesheetUsageInfoContainer>
            {!row.tnAOk ? (
              <LastUsageAndConfirmation style={{ color: lighten(theme.palette.text.disabled, 0.2) }}>
                Tool not activated
              </LastUsageAndConfirmation>
            ) : row.lastUsage ? (
              <>
                <LastUsageAndConfirmation
                  style={{
                    color: diffInCalendarDays > 5 ? lighten(theme.palette.error.main, 0.2) : theme.palette.text.primary,
                  }}
                >
                  {diffInCalendarDays === 0
                    ? `${t(`${tRP}.timesheet_usage_information_text.today`)}`
                    : diffInCalendarDays === 1
                    ? `${t(`${tRP}.timesheet_usage_information_text.yesterday`)}`
                    : `${diffInCalendarDays} ${t(`${tRP}.timesheet_usage_information_text.days_ago`)}`}
                </LastUsageAndConfirmation>
                {row.employee && <EmployeeNotificationsAndActivity employee={row.employee} />}
              </>
            ) : (
              <>
                <LastUsageAndConfirmation style={{ color: lighten(theme.palette.text.disabled, 0.2) }}>
                  Never
                </LastUsageAndConfirmation>
                {row.employee && <EmployeeNotificationsAndActivity employee={row.employee} />}
              </>
            )}
          </LastTimesheetUsageInfoContainer>
        );
      },
      // sortable: false,
    },
  ];

  const defaultItem: Partial<TnAOverviewDTO> = {
    employee: null,
    pendingApprovals: [],
    pendingEHApprovals: [],
    workedTimeBalance: 0,
    vacationBalance: 0,
    timesheetAnomalies: 0,
    lastTimesheetConfirmation: null,
    lastUsage: null,
  };

  const loadEmployeeGroup = useCallback(async () => {
    try {
      const response = await new EmployeeGroupsApiService().loadItems({
        ids: employeeGroupOption?.employeeGroupIds,
      });

      if (!response.error) {
        return response.items[0];
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    }
  }, [employeeGroupOption?.employeeGroupIds]);

  const loadTnAOverviewsData = useCallback(
    async (pageSize = 10) => {
      try {
        const response = await new TnAOverviewsApiService().loadItems(
          {
            ...legalEntityOption,
            ...employeeGroupOption,
            ...onlyCurrentEmployeesOptionValue,
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
          } as TnAOverviewsFilter,
          "",
          [],
          {
            pageNumber: 0,
            pageSize,
          }
        );

        if (!response.error) {
          return response;
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [employeeGroupOption, onlyCurrentEmployeesOptionValue, legalEntityOption]
  );

  const quickfilters = useCallback(
    (items: RefModel[], loading: boolean) => {
      return (
        <>
          <Grid sx={{ display: "flex", flexDirection: "row", width: "100%", justifyContent: "space-between" }}>
            <Grid sx={{ display: "flex", flexDirection: "row", width: "100%", gap: "5px" }}>
              {!!legalEntityQuickFilter && legalEntityQuickFilter}
              {!!employeeGroupQuickFilter && employeeGroupQuickFilter}
              {!!onlyCurrentEmployeesQuickFilter && onlyCurrentEmployeesQuickFilter}
              <ExportTnAOverviewToExcel
                loadEmployeeGroup={loadEmployeeGroup}
                loadTnAOverviewsData={loadTnAOverviewsData}
                disabled={state.loading || loading || !items?.length}
                allItems={() => allItems.current}
              />
              <SendMassNotificationsWrapper
                handleNotifyParent={forceUpdate}
                buttonVariant="contained"
                customButtonStyle={{
                  textWrap: "nowrap",
                  background:
                    state.loading || loading || !items?.length ? theme.palette.grey[300] : theme.palette.primary.main,
                  textTransform: "uppercase",
                  color:
                    state.loading || loading || !items?.length
                      ? theme.palette.grey[500]
                      : theme.palette.primary.contrastText,
                }}
                buttonTitle={t(`${tRP}.send_mass_notifications`)}
                buttonText={t(`${tRP}.send_mass_notifications`)}
                disabled={state.loading || loading || !items?.length}
              />
            </Grid>
          </Grid>
        </>
      );
    },
    [state.loading, legalEntityQuickFilter, employeeGroupQuickFilter, onlyCurrentEmployeesQuickFilter]
  );

  return (
    <Container>
      {(hasAccess || allowedToViewOnly) && (
        <LoadingOverlay
          loading={state.loading}
          error={state.error}
        >
          <Table
            translationRootPath={tRP}
            columns={columns}
            serviceApi={new TnAOverviewsApiService()}
            propsFilter={
              {
                ...legalEntityOption,
                ...employeeGroupOption,
                ...onlyCurrentEmployeesOptionValue,
                year: new Date().getFullYear(),
                month: new Date().getMonth() + 1,
              } as TnAOverviewsFilter
            }
            quickFilters={quickfilters}
            defaultItem={defaultItem}
            filter={FT_MTPAL}
            shouldReload={reload}
            readOnly
            getAllItems={(items) => {
              allItems.current = items as TnAOverviewDTO[];
              getItems(items as TnAOverviewDTO[]);
            }}
          />
        </LoadingOverlay>
      )}
    </Container>
  );
};

export default React.memo(TnAOverviews);
